// Small landing-page teaser for the fuel giveaway. Renders NOTHING while
// the campaign is inactive — this is critical: prize wording must never be
// visible to the public until the admin flips `active`.

import React from "react";
import { motion } from "framer-motion";
import { Trophy, ArrowRight, Car, MapPin, Users, Smartphone } from "lucide-react";
import { fetchPublicCampaign } from "../lib/campaign";

// Strip a leading "N ×" prefix that may already be embedded in `prize`
// (e.g. legacy production values like "2 × €50 fuel voucher"). Without this,
// the winners-count template would double-print → "2 × 2 × €50 fuel voucher".
function normalizePrize(prize) {
  if (!prize) return "";
  // Also strip a trailing plural "s" (legacy prod values store "€50 fuel vouchers")
  // so the winners-count template never renders "voucherss".
  return String(prize).replace(/^\s*\d+\s*[×x]\s*/i, "").trim().replace(/s$/i, "");
}

// Existing earning rules (values must match backend ENTRY_WEIGHTS).
const EARN_ROWS = [
  { icon: Car, label: "Save My Car", entries: "+1 / day" },
  { icon: MapPin, label: "Valid drop", entries: "+2" },
  { icon: MapPin, label: "Valid claim", entries: "+2" },
  { icon: Users, label: "Qualified referral", entries: "+3" },
  { icon: Smartphone, label: "Install / Join", entries: "+1 once" },
];

export default function CampaignTeaser() {
  const [meta, setMeta] = React.useState(null);
  React.useEffect(() => {
    fetchPublicCampaign().then(setMeta);
  }, []);

  if (!meta || !meta.active) return null;

  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="relative py-6 md:py-8 border-t border-white/5"
      data-testid="campaign-teaser"
    >
      <div className="max-w-5xl mx-auto px-5 md:px-12">
        <a
          href="/app"
          className="group flex items-start gap-4 md:gap-6 px-4 md:px-6 py-4 md:py-5 rounded-2xl bg-[#ccff00]/[0.06] border border-[#ccff00]/30 hover:bg-[#ccff00]/[0.10] transition-colors"
          data-testid="campaign-teaser-link"
        >
          <div className="shrink-0 w-11 h-11 rounded-full bg-[#ccff00]/15 border border-[#ccff00]/40 flex items-center justify-center">
            <Trophy className="w-5 h-5 text-[#ccff00]" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#ccff00] font-semibold">
              Giveaway
            </div>
            <div className="mt-0.5 text-base md:text-lg font-heading font-semibold text-white">
              Use SpotDrop. Earn Giveaway Entries.
            </div>
            <div className="text-xs md:text-sm text-zinc-400 mt-0.5" data-testid="campaign-teaser-copy">
              {meta.prize_pool ? `${meta.prize_pool} total · ` : ""}
              {meta.total_winners > 1
                ? `${meta.total_winners} × ${normalizePrize(meta.prize)}`
                : normalizePrize(meta.prize)}
            </div>

            <div className="mt-3 space-y-1.5" data-testid="campaign-teaser-earn-list">
              {EARN_ROWS.map((row) => (
                <div key={row.label} className="flex items-center justify-between gap-3 text-[11px] md:text-xs">
                  <span className="flex items-center gap-2 text-zinc-300 min-w-0">
                    <row.icon className="w-3.5 h-3.5 shrink-0 text-[#ccff00]" />
                    <span className="truncate">{row.label}</span>
                  </span>
                  <span className="font-mono text-[#ccff00] shrink-0">{row.entries}</span>
                </div>
              ))}
            </div>

            <div className="text-[11px] md:text-xs text-zinc-400 mt-2.5 leading-relaxed" data-testid="campaign-teaser-explainer">
              The more you help the parking community, the more chances you earn.
            </div>
            <div className="mt-3 inline-flex items-center gap-2 h-10 px-4 rounded-full bg-[#ccff00] text-[#0a0a0c] text-xs font-semibold group-hover:bg-[#b8e600] transition-colors" data-testid="campaign-teaser-cta">
              Start earning entries
              <ArrowRight className="w-3.5 h-3.5" />
            </div>
          </div>
        </a>
        {(meta.terms_url || true) && (
          <div className="mt-3 text-center text-[11px] text-zinc-500">
            <a
              href={meta.terms_url && !meta.terms_url.startsWith("/") ? meta.terms_url : "/giveaway-terms"}
              target={meta.terms_url && !meta.terms_url.startsWith("/") ? "_blank" : undefined}
              rel="noopener noreferrer"
              className="underline hover:text-zinc-300"
              data-testid="campaign-teaser-terms"
            >
              Giveaway terms &amp; conditions
            </a>
          </div>
        )}
      </div>
    </motion.section>
  );
}
